import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import { GripVertical, Heart, Lightbulb, Pencil, Plus, RotateCcw, Save } from 'lucide-react';
import { useAppSelector } from '../../store';
import LikedByModal from '../../components/wishlist/LikedByModal';
import SubmitWishModal from '../../components/wishlist/SubmitWishModal';
import {
  getWishlist,
  getWishlistLikes,
  reorderWishlist,
  submitWish,
  toggleWishlistLike,
  updateWish,
} from '../../services/api';
import { getSensitiveData } from '../../utils/secureStorage';
import type { CreateWishlistItemRequest, WishlistItem, WishlistLikeUser } from '../../types/wishlist';

export default function WishlistPage() {
  const navigate = useNavigate();
  const hasManagerCenterAccess = useAppSelector((state) => state.managerCenterAccess.hasAccess);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const [items, setItems] = useState<WishlistItem[]>([]);
  const [savedItems, setSavedItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [wishModalOpen, setWishModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WishlistItem | null>(null);
  const [isWishSubmitting, setIsWishSubmitting] = useState(false);
  const isWishSubmittingRef = useRef(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [likedByItem, setLikedByItem] = useState<WishlistItem | null>(null);
  const [likedByUsers, setLikedByUsers] = useState<WishlistLikeUser[]>([]);
  const [likedByLoading, setLikedByLoading] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2500);
  };

  const loadWishlist = async () => {
    const response = await getWishlist();
    if (response.success) {
      setItems(response.data || []);
      setSavedItems(response.data || []);
    } else {
      showToast(response.message || 'Failed to load wishlist.');
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadWishlist(), getSensitiveData('userId').then((id) => setCurrentUserId(id))]).finally(() =>
      setLoading(false)
    );
  }, []);

  const orderChanged = useMemo(
    () => items.some((item, index) => item.wishlistItemId !== savedItems[index]?.wishlistItemId),
    [items, savedItems]
  );

  // Tells DashboardLayout's sidebar-navigation guard to show the "unsaved changes"
  // dialog instead of navigating away while a reordered-but-unsaved sequence exists.
  useEffect(() => {
    (globalThis as typeof globalThis & { __ISX_HAS_UNSAVED_CHANGES__?: boolean }).__ISX_HAS_UNSAVED_CHANGES__ = orderChanged;
    return () => {
      (globalThis as typeof globalThis & { __ISX_HAS_UNSAVED_CHANGES__?: boolean }).__ISX_HAS_UNSAVED_CHANGES__ = false;
    };
  }, [orderChanged]);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (orderChanged) event.preventDefault();
      },
      [orderChanged]
    )
  );

  const toggleLike = async (wishlistItemId: number) => {
    setItems((current) =>
      current.map((item) =>
        item.wishlistItemId === wishlistItemId
          ? {
              ...item,
              likedByCurrentUser: !item.likedByCurrentUser,
              likeCount: item.likeCount + (item.likedByCurrentUser ? -1 : 1),
            }
          : item
      )
    );

    const response = await toggleWishlistLike(wishlistItemId);
    if (response.success && response.data) {
      const { likeCount, likedByCurrentUser } = response.data;
      setItems((current) =>
        current.map((item) => (item.wishlistItemId === wishlistItemId ? { ...item, likeCount, likedByCurrentUser } : item))
      );
    } else {
      // revert optimistic update on failure
      setItems((current) =>
        current.map((item) =>
          item.wishlistItemId === wishlistItemId
            ? {
                ...item,
                likedByCurrentUser: !item.likedByCurrentUser,
                likeCount: item.likeCount + (item.likedByCurrentUser ? -1 : 1),
              }
            : item
        )
      );
      showToast(response.message || 'Failed to update like.');
    }
  };

  const handleDragStart = (index: number) => {
    if (!hasManagerCenterAccess) return;
    setDragIndex(index);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>, index: number) => {
    if (!hasManagerCenterAccess || dragIndex === null || dragIndex === index) return;
    event.preventDefault();
  };

  const handleDrop = (index: number) => {
    if (!hasManagerCenterAccess || dragIndex === null || dragIndex === index) return;

    setItems((current) => {
      const next = [...current];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next.map((item, i) => ({ ...item, sortOrder: i + 1 }));
    });
    setDragIndex(null);
  };

  const handleSaveOrder = useCallback(async (): Promise<boolean> => {
    setSavingOrder(true);
    const response = await reorderWishlist({
      items: items.map((item) => ({ wishlistItemId: item.wishlistItemId, sortOrder: item.sortOrder })),
    });
    setSavingOrder(false);

    if (response.success) {
      setSavedItems(items);
      showToast(response.message || 'Wishlist order saved.');
      return true;
    }

    showToast(response.message || 'Failed to save wishlist order.');
    return false;
  }, [items]);

  // Fired by DashboardLayout when the user chooses "Save" on the unsaved-changes dialog.
  useEffect(() => {
    const handleSaveRequest = async (event: Event) => {
      const customEvent = event as CustomEvent<{ nextHref?: string }>;
      const nextHref = customEvent.detail?.nextHref;
      const saved = await handleSaveOrder();
      if (saved && nextHref) {
        navigate(nextHref);
      }
    };

    globalThis.addEventListener('isx-unsaved-save-request', handleSaveRequest);
    return () => globalThis.removeEventListener('isx-unsaved-save-request', handleSaveRequest);
  }, [handleSaveOrder, navigate]);

  const handleResetOrder = () => {
    setItems(savedItems);
  };

  const handleSubmitWish = async (wish: CreateWishlistItemRequest) => {
    if (isWishSubmittingRef.current) return;

    isWishSubmittingRef.current = true;
    setIsWishSubmitting(true);

    try {
      if (editingItem) {
        const response = await updateWish(editingItem.wishlistItemId, wish);
        if (response.success && response.data) {
          const updatedItem = response.data;
          const applyUpdate = (current: WishlistItem[]) =>
            current.map((item) => (item.wishlistItemId === updatedItem.wishlistItemId ? updatedItem : item));
          setItems(applyUpdate);
          setSavedItems(applyUpdate);
          setEditingItem(null);
          showToast(response.message || 'Wishlist item updated.');
        } else {
          showToast(response.message || 'Failed to update wish.');
        }
        return;
      }

      const response = await submitWish(wish);
      if (response.success && response.data) {
        const newItem = response.data;
        const appendSorted = (current: WishlistItem[]) =>
          [...current, newItem].sort((a, b) => a.sortOrder - b.sortOrder);
        setItems(appendSorted);
        setSavedItems(appendSorted);
        setWishModalOpen(false);
        showToast(response.message || 'Thanks! Your wish has been added.');
      } else {
        showToast(response.message || 'Failed to submit wish.');
      }
    } catch (error) {
      console.error('Failed to submit wishlist item:', error);
      showToast(editingItem ? 'Failed to update wish.' : 'Failed to submit wish.');
    } finally {
      isWishSubmittingRef.current = false;
      setIsWishSubmitting(false);
    }
  };

  const openLikedBy = async (item: WishlistItem) => {
    setLikedByItem(item);
    setLikedByLoading(true);
    const response = await getWishlistLikes(item.wishlistItemId);
    setLikedByUsers(response.success ? response.data || [] : []);
    setLikedByLoading(false);
  };

  const closeLikedBy = () => {
    setLikedByItem(null);
    setLikedByUsers([]);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Feature Wishlist</h1>
          <p className="mb-0 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">
            Tell us what you'd like to see next. Like the ideas that matter most to you to help us prioritize.
          </p>
        </div>
        <div className="flex gap-2">
          {hasManagerCenterAccess && orderChanged && (
            <button
              onClick={handleResetOrder}
              disabled={savingOrder}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          )}
          {hasManagerCenterAccess && orderChanged && (
            <button
              onClick={handleSaveOrder}
              disabled={savingOrder}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {savingOrder ? 'Saving...' : 'Save Order'}
            </button>
          )}
          <button
            onClick={() => setWishModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-hover"
          >
            <Plus className="h-4 w-4" />
            Submit a Wish
          </button>
        </div>
      </div>

      {hasManagerCenterAccess && (
        <div className="rounded-lg border border-brand-primary/20 bg-brand-primary/5 p-3 text-xs text-gray-600">
          Drag cards by the handle to reorder, then click <span className="font-semibold">Save Order</span> to set the priority everyone sees.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white p-10 shadow-sm">
          <p className="text-sm text-gray-500">Loading wishlist...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center shadow-sm">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
            <Lightbulb className="h-6 w-6" />
          </span>
          <p className="mt-4 text-lg font-semibold text-gray-900">No wishes yet.</p>
          <p className="mt-1 max-w-sm text-sm text-gray-500">Be the first to suggest a feature for ISentryx.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            <div
              key={item.wishlistItemId}
              draggable={hasManagerCenterAccess}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(event) => handleDragOver(event, index)}
              onDrop={() => handleDrop(index)}
              onDragEnd={() => setDragIndex(null)}
              className={`flex items-start gap-3 rounded-xl border bg-white p-5 shadow-sm transition hover:shadow-md ${
                dragIndex === index ? 'border-brand-primary opacity-60' : 'border-gray-200'
              }`}
            >
              {hasManagerCenterAccess && (
                <span className="mt-1 flex-shrink-0 cursor-grab touch-none text-gray-300 hover:text-gray-400" aria-hidden="true">
                  <GripVertical className="h-5 w-5" />
                </span>
              )}

              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500">
                {index + 1}
              </span>

              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-gray-900">{item.title}</h2>
                <p className="mt-1 max-h-40 overflow-y-auto text-sm text-gray-600 whitespace-pre-wrap break-words">
                  {item.description}
                </p>
                <p className="mt-2 text-xs text-gray-400">
                  Suggested by {item.requestedByUserId === currentUserId ? 'You' : item.requestedByName} on{' '}
                  {new Date(item.createdAt).toLocaleDateString()}
                </p>
              </div>

              <div className="flex flex-shrink-0 flex-col items-center gap-1">
                {item.canEdit && (
                  <button
                    onClick={() => setEditingItem(item)}
                    aria-label="Edit wish"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                )}
                <button
                  onClick={() => toggleLike(item.wishlistItemId)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                    item.likedByCurrentUser
                      ? 'border-red-200 bg-red-50 text-red-600'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Heart className={`h-3.5 w-3.5 ${item.likedByCurrentUser ? 'fill-red-500 text-red-500' : ''}`} />
                  {item.likeCount}
                </button>
                <button
                  onClick={() => openLikedBy(item)}
                  disabled={item.likeCount === 0}
                  className="text-[11px] font-medium text-gray-400 underline decoration-dotted transition hover:text-brand-primary disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
                >
                  Liked by
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SubmitWishModal
        isOpen={wishModalOpen || !!editingItem}
        onClose={() => {
          setWishModalOpen(false);
          setEditingItem(null);
        }}
        onSubmit={handleSubmitWish}
        isSubmitting={isWishSubmitting}
        initialValue={editingItem ? { title: editingItem.title, description: editingItem.description } : undefined}
      />

      <LikedByModal
        isOpen={!!likedByItem}
        onClose={closeLikedBy}
        title={likedByItem?.title || ''}
        users={likedByUsers}
        isLoading={likedByLoading}
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
